import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

export type Tone = 'critical' | 'warn' | 'good' | undefined;

const TONE_COLORS: Record<string, string> = {
	critical: 'error.main',
	warn: 'warning.main',
	good: 'success.main',
};

export interface Stat {
	value: string;
	label: string;
	tone?: Tone;
}

/**
 * Les deux bandeaux de l'écran ont le même gabarit, et c'est voulu : ils portent
 * deux familles de chiffres qui ne se comparent pas — un état courant au-dessus
 * du filtre, des montants mesurés en dessous — mais qui se lisent de la même
 * façon. Ce sont leur position et leur titre qui les distinguent, pas leur
 * forme.
 */
export const Band: React.FC<{
	title: string;
	note?: string;
	stats: Stat[];
}> = ({ title, note, stats }) => (
	// Fond `background.paper` et pas `#fff` : le thème admin peut passer en sombre,
	// et un blanc en dur y deviendrait un aplat illisible.
	<Box
		sx={{
			p: 2,
			mb: 2,
			bgcolor: 'background.paper',
			border: 1,
			borderColor: 'divider',
			borderRadius: 1,
			boxShadow: 1,
		}}
	>
		<Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
			<Typography variant="overline" color="text.secondary">
				{title}
			</Typography>
			{note && (
				<Typography variant="caption" color="text.secondary" fontStyle="italic">
					{note}
				</Typography>
			)}
		</Stack>

		<Stack direction="row" flexWrap="wrap" spacing={5} useFlexGap sx={{ mt: 1 }}>
			{stats.map((stat) => (
				<Box key={stat.label} sx={{ maxWidth: '14rem' }}>
					<Typography
						variant="subtitle1"
						fontWeight={600}
						color={stat.tone ? TONE_COLORS[stat.tone] : undefined}
						sx={{ lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}
					>
						{stat.value}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{stat.label}
					</Typography>
				</Box>
			))}
		</Stack>
	</Box>
);
